import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";
import { CONTACT } from "@/data/site";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", key: "home" },
  { to: "/sobe", key: "rooms" },
  { to: "/lokacija", key: "location" },
  { to: "/gallery", key: "gallery" },
  { to: "/dining", key: "dining" },
  { to: "/medjugorje", key: "medjugorje" },
  { to: "/kontakt", key: "contact" },
];

export function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="language-switcher"
      className={`flex items-center gap-1 rounded-full border border-[#1F2421]/10 bg-white/60 ${compact ? "p-1" : "p-0.5"}`}
      role="group"
      aria-label="Language"
    >
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          data-testid={`language-switcher-${l.code}`}
          onClick={() => setLang(l.code)}
          aria-pressed={lang === l.code}
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide transition-colors duration-200 ${
            lang === l.code
              ? "bg-[#4A5D3E] text-[#F9F8F3]"
              : "text-[#4A504B] hover:text-[#1F2421]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export function Header() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#F9F8F3]/85 border-b border-[#1F2421]/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center shrink-0" aria-label="Sobe Bevanda Međugorje">
          <img
            src="/images/logo-bevanda.png"
            alt="Sobe Bevanda Međugorje"
            className="h-10 sm:h-12 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Main">
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-[#4A5D3E]" : "text-[#4A504B] hover:text-[#1F2421]"
                }`
              }
            >
              {t.nav[item.key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LangSwitcher />
          </div>
          <Link
            to="/kontakt"
            data-testid="header-book-cta"
            className="hidden md:inline-flex items-center rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] text-[#F9F8F3] text-[13px] font-semibold px-5 py-2.5 transition-colors duration-200"
          >
            {t.cta.book}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Open menu"
                className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#1F2421]/10 bg-white/60 text-[#1F2421]"
              >
                <Menu size={18} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full sm:max-w-sm bg-[#F3EFE6] border-l border-[#4A5D3E]/10 p-0"
            >
              <div className="flex flex-col h-full px-8 pt-14 pb-10">
                <nav className="flex flex-col gap-1" aria-label="Mobile">
                  {NAV.map((item, i) => (
                    <Link
                      key={item.key}
                      to={item.to}
                      data-testid={`mobile-nav-link-${item.key}`}
                      onClick={() => setOpen(false)}
                      className={`font-serif-display text-3xl py-3 border-b border-[#1F2421]/8 transition-colors ${
                        location.pathname === item.to ? "text-[#4A5D3E]" : "text-[#1F2421]"
                      }`}
                      style={{ transitionDelay: `${i * 20}ms` }}
                    >
                      {t.nav[item.key]}
                    </Link>
                  ))}
                </nav>
                <div className="mt-8">
                  <LangSwitcher compact />
                </div>
                <div className="mt-auto flex flex-col gap-3">
                  <Link
                    to="/kontakt"
                    data-testid="mobile-book-cta"
                    onClick={() => setOpen(false)}
                    className="inline-flex justify-center rounded-full bg-[#4A5D3E] text-[#F9F8F3] font-semibold px-5 py-3.5"
                  >
                    {t.cta.book}
                  </Link>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="mobile-call-link"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1F2421]/15 text-[#1F2421] font-medium px-5 py-3"
                  >
                    <Phone size={16} /> {CONTACT.phoneDisplay}
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
